import { Controller } from "@hotwired/stimulus";

// The filter popover behind both record lists: pick a field, pick a value,
// apply. Every list that uses it supplies its own fields and its own form id,
// so the behaviour is defined once and cannot drift between them.
export default class extends Controller {
  static targets = ["apply", "form", "popover", "toggle", "valueHeading", "valuePanel"];
  static values = { formId: String };

  connect() {
    this.selectedAttribute = null;
    this.submitTimer = null;
  }

  disconnect() {
    window.clearTimeout(this.submitTimer);
  }

  toggle() {
    if (!this.hasPopoverTarget) return;

    const opening = this.popoverTarget.classList.contains("hidden");
    this.popoverTarget.classList.toggle("hidden", !opening);
    this.toggleTarget.setAttribute("aria-expanded", String(opening));

    if (opening && !this.selectedAttribute) {
      this.chooseFirstField();
    }
  }

  chooseFirstField() {
    const first = this.element.querySelector("[data-list-filters-attribute-param]");
    if (first) first.click();
  }

  chooseField(event) {
    const { attribute, label } = event.params;
    this.selectedAttribute = attribute;
    this.valueHeadingTarget.textContent = label;

    this.valuePanelTargets.forEach((panel) => {
      const active = panel.dataset.attribute === attribute;
      panel.classList.toggle("hidden", !active);
      panel.querySelectorAll("input, select, textarea").forEach((control) => {
        control.disabled = !active;
      });
      this.syncDateControls(panel, active);
    });

    this.element.querySelectorAll("[data-list-filters-attribute-param]").forEach((button) => {
      button.setAttribute(
        "aria-selected",
        String(button.dataset.listFiltersAttributeParam === attribute),
      );
    });
    this.syncApplyState();
  }

  selectionChanged() {
    if (this.activePanel) this.syncDateControls(this.activePanel, true);
    this.syncApplyState();
  }

  syncDateControls(panel, active) {
    const range = panel.querySelector("[data-filter-operator]:checked")?.value === "between";
    panel.querySelectorAll("[data-filter-date-mode]").forEach((field) => {
      const visible = (field.dataset.filterDateMode === "range") === range;
      field.classList.toggle("hidden", !visible);
      field.querySelector("input").disabled = !active || !visible;
    });
  }

  syncApplyState() {
    if (!this.hasApplyTarget) return;

    const panel = this.activePanel;
    const hasSelection = this.hasSelection(panel);
    this.applyTarget.disabled = !hasSelection;
  }

  applyFilter() {
    const panel = this.activePanel;
    if (!panel || !this.hasSelection(panel)) return;

    const attribute = panel.dataset.attribute;
    const values = Array.from(panel.querySelectorAll("input[data-filter-choice]:checked"))
      .map((control) => ({ operator: "eq", value: control.value }));
    const dateInput = panel.querySelector("[data-filter-value]:not(:disabled)");

    if (dateInput?.value.trim()) {
      values.push({
        operator:
          panel.querySelector(`[data-filter-operator='${attribute}']:checked`)?.value || "eq",
        value: dateInput.value.trim(),
      });
    }

    this.removeAppliedFilters(attribute);
    const nextIndex = this.nextFilterIndex();
    values.forEach(({ operator, value }, offset) => {
      this.appendHidden(nextIndex + offset, "attribute", attribute);
      this.appendHidden(nextIndex + offset, "operator", operator);
      this.appendHidden(nextIndex + offset, "value", value);
    });

    this.close();
    this.prepareFormSubmission();
    this.form.requestSubmit();
  }

  appendHidden(index, key, value, attribute = this.selectedAttribute) {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = `filters[${index}][${key}]`;
    input.value = value;
    input.dataset.listFiltersApplied = "true";
    input.dataset.listFilterAttribute = attribute;
    this.form.append(input);
  }

  removeAppliedFilters(attribute) {
    this.removeAppliedFilterGroups((group) => group.attribute === attribute);
  }

  removeFilter(event) {
    event.preventDefault();
    const {attribute, operator, value} = event.params;

    this.removeAppliedFilterGroups((group) => (
      group.attribute === attribute && group.operator === operator && group.value === value
    ));
    this.prepareFormSubmission();
    this.form.requestSubmit();
  }

  removeSearch(event) {
    event.preventDefault();
    const search = this.form.querySelector("[name='search']");
    if (search) search.value = "";

    this.prepareFormSubmission();
    this.form.requestSubmit();
  }

  clearAll(event) {
    event.preventDefault();
    this.form.querySelectorAll("[data-list-filters-applied='true']").forEach((input) => input.remove());

    const search = this.form.querySelector("[name='search']");
    if (search) search.value = "";

    this.prepareFormSubmission();
    this.form.requestSubmit();
  }

  removeAppliedFilterGroups(predicate) {
    const groups = this.appliedFilterGroups();
    groups
      .filter(predicate)
      .forEach((group) => {
        this.form.querySelectorAll(`input[name^="filters[${group.index}]"]`).forEach((input) => input.remove());
      });
  }

  appliedFilterGroups() {
    const groups = new Map();

    this.form.querySelectorAll("[data-list-filters-applied='true']").forEach((input) => {
      const index = input.name.match(/filters\[(\d+)\]/)?.[1];
      const key = input.name.match(/\[(attribute|operator|value)\]$/)?.[1];
      if (!index || !key) return;

      const group = groups.get(index) || {index};
      group[key] = input.value;
      groups.set(index, group);
    });

    return Array.from(groups.values());
  }

  nextFilterIndex() {
    const indexes = Array.from(this.form.querySelectorAll("[data-list-filters-applied='true']"))
      .map((input) => input.name.match(/filters\[(\d+)\]/)?.[1])
      .filter(Boolean)
      .map(Number);

    return indexes.length ? Math.max(...indexes) + 1 : 0;
  }

  debounceSearch() {
    window.clearTimeout(this.submitTimer);
    this.submitTimer = window.setTimeout(() => this.submit(), 300);
  }

  submit() {
    window.clearTimeout(this.submitTimer);
    this.prepareFormSubmission();
    this.form.requestSubmit();
  }

  prepareFormSubmission() {
    this.form.querySelectorAll("[data-filter-control]").forEach((control) => {
      control.disabled = true;
    });

    const search = this.form.querySelector("[data-list-filter-search]");
    if (search && search.value.trim() === "" && search.name) {
      search.dataset.listFilterOriginalName = search.name;
      search.removeAttribute("name");
    }
  }

  restoreControls() {
    const search = this.form?.querySelector("[data-list-filter-search]");
    if (search?.dataset.listFilterOriginalName) {
      search.name = search.dataset.listFilterOriginalName;
      delete search.dataset.listFilterOriginalName;
    }

    if (!this.hasValuePanelTarget) return;

    this.syncPanelSelections();
    this.valuePanelTargets.forEach((panel) => {
      const active = panel.dataset.attribute === this.selectedAttribute;
      panel.querySelectorAll("[data-filter-control]").forEach((control) => {
        control.disabled = !active;
      });
      this.syncDateControls(panel, active);
    });

    this.syncApplyState();
  }

  syncPanelSelections() {
    const filters = this.appliedFilterGroups();

    this.valuePanelTargets.forEach((panel) => {
      const attribute = panel.dataset.attribute;
      const matchingFilters = filters.filter((filter) => filter.attribute === attribute);
      const values = new Set(matchingFilters.map((filter) => filter.value));

      panel.querySelectorAll("[data-filter-choice]").forEach((control) => {
        control.checked = values.has(control.value);
      });

      const filter = matchingFilters[0];
      if (filter || panel.querySelector("[data-filter-date-mode]")) {
        panel.querySelectorAll("[data-filter-operator]").forEach((control) => {
          control.checked = control.value === (filter?.operator || "eq");
        });
      }

      panel.querySelectorAll("[data-filter-value]").forEach((dateInput) => {
        const field = dateInput.closest("[data-filter-date-mode]");
        const matchingMode = !field || (field.dataset.filterDateMode === "range") === (filter?.operator === "between");
        dateInput.value = matchingMode ? filter?.value || "" : "";

        // Keep the calendar's selection in sync too, including after removing
        // a range chip. Silent updates must not submit or reopen the filter.
        if (field) {
          const picker = this.application.getControllerForElementAndIdentifier(
            dateInput.closest('[data-controller="date-picker"]'), "date-picker",
          )?.datepickerInstance;
          picker?.clear({ silent: true });
          if (dateInput.value) {
            const dates = dateInput.value.split(" - ").map((value) => {
              if (!value.includes("/")) return new Date(`${value}T00:00:00`);
              const [day, month, year] = value.split("/").map(Number);
              return new Date(year, month - 1, day);
            });
            picker?.selectDate(dates, { silent: true });
          }
        }
      });
    });
  }

  close() {
    if (!this.hasPopoverTarget) return;

    this.popoverTarget.classList.add("hidden");
    this.toggleTarget.setAttribute("aria-expanded", "false");
  }

  closeOnOutside(event) {
    if (!this.hasPopoverTarget) return;

    // The date picker's calendar is appended to <body>, outside this element,
    // so a click on a day would close the filter before it can be applied. The
    // composed path is read because choosing a day can re-render the clicked
    // cell, which detaches event.target from the calendar.
    const inCalendar = event.composedPath().some((node) => node.classList?.contains("air-datepicker"));
    if (inCalendar) return;

    if (!this.element.contains(event.target)) this.close();
  }

  get activePanel() {
    return this.valuePanelTargets.find(
      (panel) => panel.dataset.attribute === this.selectedAttribute,
    );
  }

  hasSelection(panel) {
    if (!panel) return false;

    return Array.from(panel.querySelectorAll("input, select, textarea")).some((control) => {
      if (control.disabled) return false;
      if (control.dataset.filterChoice === "true") return control.checked;
      if (control.dataset.filterValue) {
        if (control.closest('[data-filter-date-mode="range"]')) {
          return /^\d{2}\/\d{2}\/\d{4} - \d{2}\/\d{2}\/\d{4}$/.test(control.value.trim());
        }
        return control.value.trim() !== "";
      }
      return false;
    });
  }

  get form() {
    if (this.hasFormTarget) return this.formTarget;
    return document.getElementById(this.formIdValue);
  }
}
